import { Container, Row, Col, Image, Badge } from 'react-bootstrap';
import { useLocation, Navigate } from 'react-router-dom';
import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RiFileCopyFill, RiFileImageFill, RiFileDownloadFill } from '@remixicon/react';
import Copied from '../../../components/Copied/Copied';
import Loader from '../../../components/Loader/Loader';
import { stripHtmlTags, truncateText, handleCopyToClipboard } from '../../../utils/helpers';
import InfoBanner from '../../../components/InfoBanner/InfoBanner.jsx';
import { fakeResponse } from '../../../utils/helpers';
import { mockImages } from '../../../utils/mockData';
import { authFetch } from '../../../utils/apiClient.js';
import useAccessType from '../../../hooks/useAccessType.js';
import AccessModal from '../../../components/Modal/AccessModal.jsx';
import DemoModal from '../../../components/Modal/DemoModal.jsx';

function ArticleGeneratedScreen() {
    const { t } = useTranslation();
    const location = useLocation();
    const article = location.state?.article;
    const idea = location.state?.idea;
    const timerRef = useRef(null);
    const [showCopiedModal, setShowCopiedModal] = useState(false);
    const [generatedImages, setGeneratedImages] = useState([]); // string[]
    const [isGeneratingImage, setIsGeneratingImage] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const { getAccessType, isDemo } = useAccessType();
    const [modalType, setModalType] = useState(null);

    if (!article || !idea) {
        return <Navigate to="/" />;
    }

    async function generateImage(prompt) {
        try {
            const response = await authFetch("/api/article/generate-image", {
                method: "POST",
                body: JSON.stringify({ prompt })
            });

            return await response.json();
        } catch (error) {
            console.error("Error generating image:", error);
            return null;
        }
    }

    const handleDemo = async () => {
        setIsGeneratingImage(true);
        const data = await fakeResponse(mockImages)
        if (data.length > 0) {
            const imageBase64 = data[0];
            setGeneratedImages(prev => [...prev, `data:image/png;base64,${imageBase64}`]);
        }
        setIsGeneratingImage(false);
    };

    const handleGenerateImage = async () => {
        if (isDemo) {
            setShowModal(true);
            return;
        }
        const accessType = getAccessType();
        if (accessType) {
            setModalType(accessType);
            setShowModal(true);
            return;
        }

        setIsGeneratingImage(true);
        const data = await generateImage(truncateText(stripHtmlTags(article.articleHtml)));

        if (data.length > 0) {
            const imageBase64 = data[0];
            setGeneratedImages(prev => [...prev, `data:image/png;base64,${imageBase64}`]);
        }
        setIsGeneratingImage(false);
    };

    const handleDownloadImage = async (imageSource, index) => {
        const link = document.createElement('a');

        link.href = imageSource;
        link.download = `generated-image-${index + 1}.png`;
        link.click();
    };

    return (
        <>
            <Container className="py-5">
                {isDemo && (
                    <InfoBanner />
                )}
                <Row className="mb-4">
                    <Col xs={12} className="d-flex">
                        <div className="card-custom-content">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h3 className="card-title text-uppercase">{t('article.title')}</h3>
                                <RiFileCopyFill
                                    size={18}
                                    onClick={() => handleCopyToClipboard(idea, timerRef, setShowCopiedModal)}
                                    role="button"
                                    className="copy-icon"
                                    title={t('buttons.copy')}
                                />
                            </div>
                            <p className="card-text">{idea}</p>
                        </div>
                    </Col>
                </Row>

                <Row className="mb-4 g-4">
                    <Col xs={12} md={12} lg={6} className="d-flex">
                        <div className="card-custom-content">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h3 className="card-title text-uppercase">{t('article.slug')}</h3>
                                <RiFileCopyFill
                                    size={18}
                                    onClick={() => handleCopyToClipboard(article.slug, timerRef, setShowCopiedModal)}
                                    role="button"
                                    className="copy-icon"
                                    title={t('buttons.copy')}
                                />
                            </div>
                            <p className="card-text">{article.slug}</p>
                        </div>
                    </Col>
                    <Col xs={12} md={12} lg={6} className="d-flex">
                        <div className="card-custom-content">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h3 className="card-title text-uppercase">{t('article.metaDesc')}</h3>
                                <RiFileCopyFill
                                    size={18}
                                    onClick={() => handleCopyToClipboard(article.metaDescription, timerRef, setShowCopiedModal)}
                                    role="button"
                                    className="copy-icon"
                                    title={t('buttons.copy')}
                                />
                            </div>
                            <p className="card-text">{article.metaDescription}</p>
                        </div>
                    </Col>
                </Row>

                <Row className="mb-4">
                    <Col xs={12} className="d-flex">
                        <div className="card-custom-content">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div className="d-flex align-items-center gap-2">
                                    <h3 className="card-title text-uppercase">{t('app.content')}</h3>
                                    <Badge bg="light" text="dark">{t('app.html')}</Badge>
                                </div>
                                <RiFileCopyFill
                                    size={20}
                                    onClick={() => handleCopyToClipboard(article.articleHtml, timerRef, setShowCopiedModal)}
                                    role="button"
                                    className="copy-icon"
                                    title={t('article.copyHtml')}
                                />
                            </div>
                            <div className="max-h-600 overflow-y-auto hide-scrollbar" dangerouslySetInnerHTML={{ __html: article.articleHtml }} />
                        </div>
                    </Col>
                </Row>

                <Row className="g-4">
                    {/* Generate Image Card - Always First */}
                    <Col xs={12} lg={6} className="d-flex">
                        <div className="card-custom-content d-flex flex-column min-h-400">
                            <div className="d-flex align-items-center justify-content-center flex-grow-1">
                                <RiFileImageFill
                                    size={30}
                                    role="button"
                                    className="copy-icon"
                                    title={t('article.generateImage')}
                                    onClick={!isGeneratingImage ? handleGenerateImage : undefined}
                                    style={isGeneratingImage ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                                />
                            </div>
                        </div>
                    </Col>

                    {/* Generated Images - Une seule boucle */}
                    {generatedImages.map((imageSrc, index) => (
                        <Col xs={12} lg={6} key={`img-${index}`} className="d-flex">
                            <div className="card-custom-content min-h-400">
                                <div className="d-flex align-items-center justify-content-end">
                                    <RiFileDownloadFill
                                        size={20}
                                        role="button"
                                        className="copy-icon"
                                        title={t('buttons.download')}
                                        onClick={() => handleDownloadImage(imageSrc, index)}
                                    />
                                </div>
                                <div className="d-flex align-items-center justify-content-center h-100">
                                    <Image
                                        src={imageSrc}
                                        alt={`Generated image ${index + 1}`}
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '100%',
                                            objectFit: 'contain'
                                        }}
                                    />
                                </div>
                            </div>
                        </Col>
                    ))}

                    {/* Loading Card */}
                    {isGeneratingImage && (
                        <Col xs={12} lg={6} className="d-flex">
                            <Loader className="card-custom-content min-h-400" />
                        </Col>
                    )}
                </Row>

                <AccessModal
                    show={showModal && !isDemo}
                    setShow={setShowModal}
                    modalType={modalType}
                />

                <DemoModal
                    show={showModal && isDemo}
                    setShow={setShowModal}
                    onAction={handleDemo}
                />
            </Container>

            <Copied
                show={showCopiedModal}
                onHide={() => setShowCopiedModal(false)}
            />
        </>

    );
}

export default ArticleGeneratedScreen;